/* eslint-disable no-undef */
import { faBath } from '@fortawesome/pro-light-svg-icons/faBath';
import { faBedAlt } from '@fortawesome/pro-light-svg-icons/faBedAlt';
import { faCouch } from '@fortawesome/pro-light-svg-icons/faCouch';
import { Property } from '@propertypal/shared/src/types/property';
import puralise from '@propertypal/shared/src/utils/puralise';
import Link from 'next/link';
import React, { CSSProperties, FunctionComponent, useRef } from 'react';
import { useTheme } from 'styled-components';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import OpenViewingOverlay from '../property-box/OpenViewingOverlay';
import PropertyFavourite from '../property-box/PropertyFavourite';
import ShowHomesBox from '../property-box/ShowHomesBox';
import { Heading, Text } from '../typography';
import PropertyAgentBox from './PropertyAgentBox';
import PropertyPrice from './PropertyPrice';
import { Container, KeyDetails, SummaryItem, SummaryBox } from './PropertySummary.style';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string) => void;
  onEnquiryClick: () => void;
  onRegisterInterestClick: () => void;
  onRequestValuationClick: () => void;
  registerInterest?: boolean;
  requestValuation?: boolean;
  favourited: boolean;
  toggleFavourited: (favourited: boolean) => Promise<boolean>;
  leadId: number;
}

const PropertySummary: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  // const agentAward = sellingAgent?.propertypalAwards?.length ? sellingAgent?.propertypalAwards[0] : null;
  const keyDetails = useRef<HTMLDivElement>(null);
  const showKeyDetails =
    props.property.numBedrooms ||
    props.property.numBathrooms ||
    props.property.numReceptionRooms ||
    props.property.epc ||
    props.property.ber;

  // only add comma add end of address if address is more than 1 word
  // particularly will find this for ROI properties
  const addressAppend = () => {
    // handles potentially hyphenated addresses
    const words = props.property.displayAddressLine1.replace('-', '').match(/(\w+)/g);
    if (words && words.length > 1) return ',';
    return null;
  };

  return (
    <Container className="pp-property-summary">
      <SummaryBox>
        {props.property.openViewing && (
          <OpenViewingOverlay
            containerStyle={{ marginRight: 10, marginBottom: 15 }}
            openViewing={props.property.openViewing}
            themeName="light"
          />
        )}

        <Heading fontWeight="600" data-testid="address-line-1" style={{ position: 'relative', paddingRight: 45 }}>
          {props.property.type === 'property' && (
            <PropertyFavourite
              containerStyle={{ position: 'absolute', top: 0, right: 0, zIndex: 10 }}
              favourited={props.favourited}
              onFavourite={props.toggleFavourited}
              showBackground
            />
          )}

          {props.property.displayAddressLine1}
          {addressAppend()}
        </Heading>

        <Text fontSize={24} color={theme.textLight} data-testid="address-line-2">
          {props.property.displayAddressLine2}
        </Text>

        <Text fontSize={18} mt={10} color={theme.textLight} data-testid="property-brief-text">
          {props.property.briefText}
        </Text>

        {props.property.development && (
          <Text fontSize={16} mt={15} color={theme.primary} className="pp-development-link-text">
            <Link href={props.property.development.path} style={{ textDecoration: 'underline' }}>
              This property forms part of the <b>{props.property.development.name}</b> development <Chevron size={9} />
            </Link>
          </Text>
        )}

        <PropertyPrice
          fontSize={24}
          price={props.property.price}
          alternativeCurrencyPrice={props.property.alternativeCurrencyPrice}
          containerStyle={{ marginTop: 40 }}
          status={props.property.status}
          comingSoonText={props.property.comingSoonText}
          statusButton
        />

        {showKeyDetails && (
          <KeyDetails ref={keyDetails}>
            {!!props.property.numBedrooms && (
              <SummaryItem>
                <FontAwesomeIcon
                  icon={faBedAlt}
                  color={theme.backgroundMid}
                  className="pp-summary-icon pp-summary-icon-beds"
                  size={26}
                />

                <Text ml={10}>
                  {props.property.numBedrooms} {puralise('Bedroom', 'Bedrooms', props.property.numBedrooms)}
                </Text>
              </SummaryItem>
            )}

            {!!props.property.numBathrooms && (
              <SummaryItem>
                <FontAwesomeIcon
                  icon={faBath}
                  color={theme.backgroundMid}
                  className="pp-summary-icon pp-summary-icon-baths"
                  size={26}
                />

                <Text ml={10}>
                  {props.property.numBathrooms} {puralise('Bathroom', 'Bathrooms', props.property.numBathrooms)}
                </Text>
              </SummaryItem>
            )}

            {!!props.property.numReceptionRooms && (
              <SummaryItem>
                <FontAwesomeIcon
                  icon={faCouch}
                  color={theme.backgroundMid}
                  className="pp-summary-icon pp-summary-icon-receptions"
                  size={26}
                />

                <Text ml={10}>
                  {props.property.numReceptionRooms}{' '}
                  {puralise('Reception', 'Receptions', props.property.numReceptionRooms)}
                </Text>
              </SummaryItem>
            )}
          </KeyDetails>
        )}

        <ShowHomesBox property={props.property} containerStyle={{ marginTop: 30 }} />

        {/* {(property.type === 'development' || property.development) && (
            <AgentAwards awards={property.account.propertypalAwards} containerStyle={{ marginTop: 30 }} />
          )} */}
      </SummaryBox>

      <PropertyAgentBox {...props} />
    </Container>
  );
};

export default PropertySummary;
